import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Sparkles, Target, Users, Shield, Settings, FileCheck, Zap, TrendingUp, ClipboardCheck } from 'lucide-react';

const ITBereichsleiter = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IT-Bereichsleiter Mönchengladbach',
    description: 'Externer IT-Bereichsleiter für KMU in Mönchengladbach: Struktur, Führung, Verantwortung für Ihre IT-Abteilung.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'IT-Management'
  };

  const aufgaben = [
    { icon: Users, text: 'Führung und Koordination der IT-Mitarbeiter' },
    { icon: Settings, text: 'Aufbau klarer Prozesse und Verantwortlichkeiten' },
    { icon: ClipboardCheck, text: 'Priorisierung von Aufgaben und Projekten' },
    { icon: Shield, text: 'Qualitäts- und Sicherheitsmanagement' },
    { icon: TrendingUp, text: 'Schnittstelle zwischen Geschäftsführung und IT' },
    { icon: FileCheck, text: 'Einführung von Standards und Best Practices' }
  ];

  const vorteile = [
    'Entlastung der Geschäftsführung',
    'Professionalisierung der IT-Strukturen',
    'Klare Verantwortlichkeiten',
    'Objektive Entscheidungen ohne Betriebsblindheit'
  ];

  const relatedPages = [
    { title: 'IT-Leiter für KMU', url: '/it-leiter-kmu', description: 'Strategische IT-Führung für Ihr Unternehmen' },
    { title: 'Interims IT-Leiter', url: '/interims-it-leiter', description: 'Schnelle Übernahme bei Ausfall oder Krise' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="IT-Bereichsleiter Mönchengladbach – Externe IT-Führung für KMU"
        description="Externer IT-Bereichsleiter für KMU in Mönchengladbach: Struktur, Führung, Verantwortung. Professionelle IT-Abteilungsleitung im Raum Mönchengladbach, Viersen, Krefeld."
        keywords="IT-Bereichsleiter Mönchengladbach, IT-Führung KMU, IT-Abteilungsleitung extern, IT-Organisation, externer IT-Leiter, Mönchengladbach, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400/15 to-blue-500/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              IT-Bereichsleitung
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Externer </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">IT-Bereichsleiter</span>
              <span className="text-slate-900"> in Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Als IT-Bereichsleiter übernehme ich die organisatorische und fachliche Führung Ihrer IT-Abteilung – für Unternehmen in Mönchengladbach, Viersen und Krefeld. Unabhängig davon, ob Ihre IT aus einem oder mehreren Mitarbeitern besteht.
            </p>
          </div>
        </div>
      </section>

      {/* Aufgaben Section */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Settings className="w-4 h-4 mr-2" />
              Aufgaben
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Meine Aufgaben als IT-Bereichsleiter
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {aufgaben.map((item, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                      <item.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <p className="text-slate-700 leading-relaxed pt-2">
                      {item.text}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Warum extern */}
          <div className="mb-20">
            <Card className="bg-gradient-to-br from-slate-50 to-cyan-50/30 border-0 shadow-xl">
              <CardContent className="p-12 lg:p-16">
                <div className="text-center mb-12">
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                    Warum ein externer Bereichsleiter?
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {vorteile.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Related Pages */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                <Target className="w-4 h-4 mr-2" />
                Weitere Leistungen
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Das könnte Sie auch interessieren
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {relatedPages.map((page, index) => (
                <Link key={index} to={page.url}>
                  <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                        {page.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{page.description}</p>
                      <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                        <span className="text-sm font-medium">Mehr erfahren</span>
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Sie möchten Ihre IT-Abteilung professionalisieren?
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Ich unterstütze Sie gerne.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Kontakt aufnehmen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ITBereichsleiter;